/**
 * @file WarehousePoliciesPanel.tsx
 * @description Вкладка «Правила» настроек склада: порядок списания партий,
 * маркировка, отрицательные остатки, скан ячейки и пороги срока годности
 * (вынесено из `WmsSettingsPage` в phase19.23 этап A; phase19.31 зона
 * «Склад» — переключатели вместо чекбоксов, тексты по-русски, «Сохранить» в
 * верхней панели).
 *
 * Панель — раскладка формы; состояние, проверка и запрос — в
 * `useWarehousePolicies`. Заголовок страницы с «Сохранить» рисует панель:
 * только она знает, есть ли изменения.
 */

import type { ReactNode } from "react";

import { SETTINGS_CARD_CLASS, SettingNumberRow, SettingSwitchRow } from "./SettingRows";
import { StaleDraftDaysField } from "./StaleDraftDaysField";
import { useWarehousePolicies } from "./useWarehousePolicies";
import { WmsSettingsHeader } from "./WmsSettingsHeader";
import { Button } from "../../ui/button";
import type { WarehouseDto } from "../../../lib/api-wms";

interface Props {
  warehouse: WarehouseDto;
  onUpdated: (fresh: WarehouseDto) => void;
  /** Строка «Склад ▾» — первой строкой формы (её рисует страница). */
  warehousePicker?: ReactNode;
}

export function WarehousePoliciesPanel({ warehouse, onUpdated, warehousePicker }: Props) {
  const form = useWarehousePolicies(warehouse, onUpdated);
  const { draft, patch } = form;

  return (
    <>
      <WmsSettingsHeader
        action={
          <Button variant="brand" onClick={() => void form.save()} disabled={!form.canSave} loading={form.saving}>
            Сохранить
          </Button>
        }
      />
      <div className="flex max-w-[45rem] flex-col gap-1">
        {warehousePicker}
        {/* SC69: строки правил — на непрозрачной карточке, не на фоне кабинета. */}
        <div className={SETTINGS_CARD_CLASS}>
          <SettingSwitchRow
            label="Сначала списывать партии с ближним сроком"
            hint="Сборка и списание предлагают сначала партии, у которых срок годности ближе"
            checked={draft.defaultFefo}
            onChange={(defaultFefo) => patch({ defaultFefo })}
          />
          <SettingSwitchRow
            label="Маркировка «Честный знак» обязательна"
            hint="Пока только сохраняется: проверка кода маркировки при приёмке появится позже"
            checked={draft.crptRequired}
            onChange={(crptRequired) => patch({ crptRequired })}
          />
          <SettingSwitchRow
            label="Разрешить отрицательные остатки"
            hint="Включено — продажа и упаковка проходят даже при нехватке остатка, остаток уходит в минус. Выключено — операция останавливается с ошибкой «недостаточно остатка». Выключайте только после сверки остатков склада."
            checked={draft.allowNegativeStock}
            onChange={(allowNegativeStock) => patch({ allowNegativeStock })}
          />
          <SettingSwitchRow
            label="Требовать скан ячейки при сборке"
            hint="Кладовщик подтверждает ячейку сканом перед отметкой позиции"
            checked={draft.requireBinScanOnPick}
            onChange={(requireBinScanOnPick) => patch({ requireBinScanOnPick })}
          />
          <SettingNumberRow
            label="Предупреждать за N дней до истечения срока"
            value={draft.expiryAlertDays}
            onChange={(expiryAlertDays) => patch({ expiryAlertDays })}
            min={1}
            max={365}
          />
          <SettingNumberRow
            label="Срочно — за N дней до истечения срока"
            value={draft.expiryCriticalDays}
            onChange={(expiryCriticalDays) => patch({ expiryCriticalDays })}
            min={1}
            max={60}
          />
          <StaleDraftDaysField
            value={draft.staleDraftDays}
            error={form.staleError}
            onChange={(staleDraftDays) => patch({ staleDraftDays })}
          />
        </div>
      </div>
    </>
  );
}
